import { useEffect, useMemo, useState, type FormEvent } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { uploadContentMedia } from "@/lib/api";
import {
  blocksToBodyText,
  bodyTextToBlocks,
  createEditorialAuthor,
  loadContentHistory,
  loadEditorialWorkspace,
  saveContentDraft,
  transitionContent,
  type ContentAuthor,
  type ContentDraft,
  type ContentEntry,
  type ContentRevision,
  type ContentStatus,
  type ContentWorkflowEvent,
} from "@/lib/content-api";

const emptyDraft: ContentDraft = {
  content_type: "blog",
  title: "",
  slug: "",
  route_path: "",
  excerpt: "",
  author_id: "",
  reviewer_id: "",
  hero_image_url: "",
  body_text: "",
  category: "",
  tags: "",
  canonical_url: "",
  seo_title: "",
  meta_description: "",
  og_title: "",
  og_description: "",
  og_image_url: "",
  robots: "noindex_nofollow",
  structured_data_type: "article",
  availability: "planned",
};

function entryDraft(entry: ContentEntry): ContentDraft {
  return {
    content_type: entry.content_type,
    title: entry.title,
    slug: entry.slug,
    route_path: entry.route_path,
    excerpt: entry.excerpt,
    author_id: entry.author_id || "",
    reviewer_id: entry.reviewer_id || "",
    hero_image_url: entry.hero_image_url || "",
    body_text: blocksToBodyText(entry.body),
    category: entry.category || "",
    tags: entry.tags.join(", "),
    canonical_url: entry.canonical_url || "",
    seo_title: entry.seo_title,
    meta_description: entry.meta_description,
    og_title: entry.og_title || "",
    og_description: entry.og_description || "",
    og_image_url: entry.og_image_url || "",
    robots: entry.robots,
    structured_data_type: entry.structured_data_type,
    availability: entry.availability,
  };
}

function formatDate(value?: string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function workflowActions(entry: ContentEntry) {
  const actions: { status: ContentStatus; label: string }[] = [];
  if (entry.status === "draft")
    actions.push({ status: "editorial_review", label: "Submit editorial review" });
  if (entry.status === "editorial_review")
    actions.push(
      { status: "draft", label: "Return to draft" },
      { status: "seo_review", label: "Send to SEO review" },
    );
  if (entry.status === "seo_review")
    actions.push(
      { status: "editorial_review", label: "Return to editorial" },
      { status: "approved", label: "Approve content" },
    );
  if (entry.status === "approved")
    actions.push(
      { status: "draft", label: "Return to draft" },
      { status: "scheduled", label: "Schedule publication" },
      { status: "published", label: "Publish now" },
      { status: "archived", label: "Archive" },
    );
  if (entry.status === "scheduled")
    actions.push(
      { status: "approved", label: "Cancel schedule" },
      { status: "published", label: "Publish due item" },
      { status: "archived", label: "Archive" },
    );
  if (["published", "updated"].includes(entry.status))
    actions.push(
      { status: "draft", label: "Start governed update" },
      { status: "archived", label: "Archive" },
    );
  if (entry.status === "archived")
    actions.push({ status: "draft", label: "Restore as draft" });
  return actions;
}

export function ContentEditor({ platformOwner }: { platformOwner: boolean }) {
  const [entries, setEntries] = useState<ContentEntry[]>([]);
  const [authors, setAuthors] = useState<ContentAuthor[]>([]);
  const [selected, setSelected] = useState<ContentEntry>();
  const [draft, setDraft] = useState<ContentDraft>(emptyDraft);
  const [revisions, setRevisions] = useState<ContentRevision[]>([]);
  const [events, setEvents] = useState<ContentWorkflowEvent[]>([]);
  const [filter, setFilter] = useState<ContentStatus | "all">("all");
  const [query, setQuery] = useState("");
  const [changeNote, setChangeNote] = useState("");
  const [scheduledFor, setScheduledFor] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [authorRole, setAuthorRole] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function load(focusId?: string) {
    try {
      const workspace = await loadEditorialWorkspace();
      setEntries(workspace.entries);
      setAuthors(workspace.authors);
      const focus = focusId
        ? workspace.entries.find((entry) => entry.id === focusId)
        : undefined;
      if (focus) selectEntry(focus);
    } catch (reason) {
      setError((reason as Error).message);
    }
  }

  useEffect(() => {
    if (platformOwner) void load();
  }, [platformOwner]);

  useEffect(() => {
    if (!selected) {
      setRevisions([]);
      setEvents([]);
      return;
    }
    let live = true;
    loadContentHistory(selected.id)
      .then((history) => {
        if (!live) return;
        setRevisions(history.revisions);
        setEvents(history.events);
      })
      .catch((reason: Error) => live && setError(reason.message));
    return () => {
      live = false;
    };
  }, [selected?.id]);

  function selectEntry(entry: ContentEntry) {
    setSelected(entry);
    setDraft(entryDraft(entry));
    setChangeNote("");
    setScheduledFor(
      entry.scheduled_for
        ? new Date(entry.scheduled_for).toISOString().slice(0, 16)
        : "",
    );
  }

  function newEntry() {
    setSelected(undefined);
    setDraft(emptyDraft);
    setChangeNote("");
    setScheduledFor("");
    setRevisions([]);
    setEvents([]);
  }

  const visibleEntries = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return entries.filter(
      (entry) =>
        (filter === "all" || entry.status === filter) &&
        (!normalized ||
          [entry.title, entry.route_path, entry.content_type, entry.status].some(
            (value) => value.toLowerCase().includes(normalized),
          )),
    );
  }, [entries, filter, query]);

  const counts = useMemo(
    () =>
      entries.reduce<Record<string, number>>((result, entry) => {
        result[entry.status] = (result[entry.status] || 0) + 1;
        return result;
      }, {}),
    [entries],
  );
  const preview = useMemo(
    () => bodyTextToBlocks(draft.body_text),
    [draft.body_text],
  );
  const editable = !selected || selected.status === "draft";

  if (!platformOwner)
    return (
      <div className="rounded-2xl border bg-white p-8">
        <h1 className="font-serif text-3xl">Editorial desk</h1>
        <p className="mt-3 text-sm text-slate-500">
          Platform administrator access is required.
        </p>
      </div>
    );

  function field<K extends keyof ContentDraft>(key: K, value: ContentDraft[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const saved = await saveContentDraft(
        selected?.id || null,
        draft,
        changeNote,
      );
      await load(saved.id);
      setChangeNote("");
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function move(status: ContentStatus) {
    if (!selected) return;
    setBusy(true);
    setError("");
    try {
      const moved = await transitionContent(
        selected.id,
        status,
        changeNote,
        status === "scheduled" && scheduledFor
          ? new Date(scheduledFor).toISOString()
          : undefined,
      );
      await load(moved.id);
      setChangeNote("");
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function addAuthor() {
    setBusy(true);
    setError("");
    try {
      const author = await createEditorialAuthor(authorName, authorRole);
      setAuthorName("");
      setAuthorRole("");
      await load(selected?.id);
      field("author_id", author.id);
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function media(file?: File) {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      field("hero_image_url", await uploadContentMedia(file));
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-5">
      <header>
        <p className="eyebrow">PUBLIC CONTENT CONTROL</p>
        <h1 className="mt-2 font-serif text-4xl">Editorial desk</h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-500">
          Govern drafts, independent review, search readiness, publication
          windows, revisions and public snapshots. This is the RAQOZ CMS;
          distribution remains separate until the Growth Scheduler contract is
          stable.
        </p>
      </header>

      {error && (
        <p className="rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</p>
      )}

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          ["Working drafts", counts.draft || 0],
          ["In review", (counts.editorial_review || 0) + (counts.seo_review || 0)],
          ["Approved / scheduled", (counts.approved || 0) + (counts.scheduled || 0)],
          ["Published", (counts.published || 0) + (counts.updated || 0)],
        ].map(([label, value]) => (
          <div className="rounded-2xl border bg-white p-4" key={label}>
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
              {label}
            </p>
            <p className="mt-2 text-3xl font-semibold">{value}</p>
          </div>
        ))}
      </section>

      <section className="grid gap-5 xl:grid-cols-[.7fr_1.3fr]">
        <aside className="grid content-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <Button onClick={newEntry}>New working draft</Button>
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
            <Input
              aria-label="Search editorial entries"
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search title or route"
              value={query}
            />
            <Select
              aria-label="Filter editorial status"
              onChange={(event) => setFilter(event.target.value as ContentStatus | "all")}
              value={filter}
            >
              <option value="all">All statuses</option>
              <option value="draft">Draft</option>
              <option value="editorial_review">Editorial review</option>
              <option value="seo_review">SEO review</option>
              <option value="approved">Approved</option>
              <option value="scheduled">Scheduled</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </Select>
          </div>
          <div className="max-h-[42rem] overflow-y-auto pr-1">
            {visibleEntries.map((entry) => (
              <button
                className="mb-2 w-full rounded-xl border border-slate-100 p-3 text-left hover:border-emerald-300"
                key={entry.id}
                onClick={() => selectEntry(entry)}
                type="button"
              >
                <strong className="block text-sm">{entry.title}</strong>
                <small className="mt-1 block text-slate-500">
                  {entry.route_path}
                </small>
                <span className="mt-2 flex items-center justify-between gap-2">
                  <Badge value={entry.status} />
                  <small className="text-slate-400">
                    {formatDate(entry.updated_at)}
                  </small>
                </span>
              </button>
            ))}
            {!visibleEntries.length && (
              <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
                No content matches this queue.
              </p>
            )}
          </div>
        </aside>

        <div className="grid gap-5">
          <form
            className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
            onSubmit={save}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-serif text-2xl">
                  {selected ? selected.title : "New content"}
                </h2>
                <p className="mt-1 text-xs text-slate-500">
                  {selected
                    ? `${selected.status.replaceAll("_", " ")} · revision ${revisions[0]?.revision_number || 0}`
                    : "Working draft"}
                </p>
              </div>
              {selected && <Badge value={selected.status} />}
            </div>

            {!editable && (
              <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-800">
                This state is locked. Use the governed workflow controls below
                to return it to a working draft or move it forward.
              </p>
            )}

            <fieldset className="grid gap-4" disabled={!editable || busy}>
              <div className="grid gap-4 md:grid-cols-2">
                <Label>
                  Content type
                  <Select
                    onChange={(event) => field("content_type", event.target.value)}
                    value={draft.content_type}
                  >
                    <option value="page">Page</option>
                    <option value="blog">Article</option>
                    <option>tutorial</option>
                    <option value="guide">Guide</option>
                    <option value="template">Template</option>
                    <option value="comparison">Comparison</option>
                    <option value="industry">Industry</option>
                    <option value="glossary">Glossary</option>
                    <option value="methodology">Methodology</option>
                  </Select>
                </Label>
                <Label>
                  Availability
                  <Select
                    onChange={(event) =>
                      field("availability", event.target.value as ContentEntry["availability"])
                    }
                    value={draft.availability}
                  >
                    <option value="planned">Planned</option>
                    <option value="coming_soon">Coming soon</option>
                    <option value="beta">Beta</option>
                    <option value="available">Available</option>
                  </Select>
                </Label>
              </div>
              <Label>
                Title
                <Input
                  onChange={(event) => field("title", event.target.value)}
                  required
                  value={draft.title}
                />
              </Label>
              <div className="grid gap-4 md:grid-cols-2">
                <Label>
                  Slug
                  <Input
                    onChange={(event) => field("slug", event.target.value)}
                    pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
                    required
                    value={draft.slug}
                  />
                </Label>
                <Label>
                  Route path
                  <Input
                    onChange={(event) => field("route_path", event.target.value)}
                    placeholder="/guides/example"
                    required
                    value={draft.route_path}
                  />
                </Label>
              </div>
              <Label>
                Excerpt
                <Textarea
                  onChange={(event) => field("excerpt", event.target.value)}
                  required
                  value={draft.excerpt}
                />
              </Label>
              <div className="grid gap-4 md:grid-cols-2">
                <Label>
                  Author
                  <Select
                    onChange={(event) => field("author_id", event.target.value)}
                    value={draft.author_id}
                  >
                    <option value="">Select author</option>
                    {authors.map((author) => (
                      <option key={author.id} value={author.id}>
                        {author.display_name}
                      </option>
                    ))}
                  </Select>
                </Label>
                <Label>
                  Independent reviewer
                  <Select
                    onChange={(event) => field("reviewer_id", event.target.value)}
                    value={draft.reviewer_id}
                  >
                    <option value="">Select reviewer</option>
                    {authors.map((author) => (
                      <option key={author.id} value={author.id}>
                        {author.display_name}
                      </option>
                    ))}
                  </Select>
                </Label>
              </div>
              <Label>
                Body blocks
                <Textarea
                  className="min-h-56"
                  onChange={(event) => field("body_text", event.target.value)}
                  placeholder={"## Heading\n\nParagraph\n\n- Bullet\n\n> Quote\n\n! Callout"}
                  required
                  value={draft.body_text}
                />
              </Label>
              <div className="grid gap-4 md:grid-cols-2">
                <Label>
                  Category
                  <Input
                    onChange={(event) => field("category", event.target.value)}
                    value={draft.category}
                  />
                </Label>
                <Label>
                  Tags
                  <Input
                    onChange={(event) => field("tags", event.target.value)}
                    placeholder="controls, procurement"
                    value={draft.tags}
                  />
                </Label>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <Label>
                  SEO title ({draft.seo_title.length}/70)
                  <Input
                    maxLength={70}
                    onChange={(event) => field("seo_title", event.target.value)}
                    required
                    value={draft.seo_title}
                  />
                </Label>
                <Label>
                  Canonical URL
                  <Input
                    onChange={(event) => field("canonical_url", event.target.value)}
                    placeholder="https://raqoz.com/..."
                    type="url"
                    value={draft.canonical_url}
                  />
                </Label>
              </div>
              <Label>
                Meta description ({draft.meta_description.length}/180)
                <Textarea
                  maxLength={180}
                  onChange={(event) => field("meta_description", event.target.value)}
                  required
                  value={draft.meta_description}
                />
              </Label>
              <div className="grid gap-4 md:grid-cols-2">
                <Label>
                  Robots
                  <Select
                    onChange={(event) =>
                      field("robots", event.target.value as ContentEntry["robots"])
                    }
                    value={draft.robots}
                  >
                    <option value="noindex_nofollow">Noindex, nofollow</option>
                    <option value="noindex_follow">Noindex, follow</option>
                    <option value="index_follow">Index, follow</option>
                  </Select>
                </Label>
                <Label>
                  Structured data
                  <Select
                    onChange={(event) =>
                      field("structured_data_type", event.target.value)
                    }
                    value={draft.structured_data_type}
                  >
                    <option value="web_page">Web page</option>
                    <option value="article">Article</option>
                    <option value="faq">FAQ</option>
                    <option value="software_application">Software application</option>
                    <option value="product_offer">Product offer</option>
                    <option value="how_to">How-to</option>
                    <option value="none">None</option>
                  </Select>
                </Label>
              </div>
              <Label>
                Hero media
                <input
                  accept="image/png,image/jpeg,image/webp,image/avif"
                  className="mt-2 block text-sm"
                  onChange={(event) => void media(event.target.files?.[0])}
                  type="file"
                />
                <Input
                  onChange={(event) => field("hero_image_url", event.target.value)}
                  placeholder="Uploaded media URL"
                  value={draft.hero_image_url}
                />
              </Label>
              <Label>
                Revision note
                <Input
                  minLength={8}
                  onChange={(event) => setChangeNote(event.target.value)}
                  placeholder="What changed and why"
                  required
                  value={changeNote}
                />
              </Label>
              <div className="flex justify-end gap-2">
                <Button onClick={newEntry} type="button" variant="outline">
                  Clear
                </Button>
                <Button disabled={busy} type="submit">
                  {busy ? "Saving…" : "Save revision"}
                </Button>
              </div>
            </fieldset>
          </form>

          <details className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" open>
            <summary className="cursor-pointer font-bold">Content preview</summary>
            <article className="prose mt-5 max-w-none">
              <h1>{draft.title || "Your title"}</h1>
              <p>{draft.excerpt || "The excerpt appears here."}</p>
              {preview.map((block, index) => {
                if (block.type === "heading") return <h2 key={index}>{block.text}</h2>;
                if (block.type === "bullet") return <li key={index}>{block.text}</li>;
                if (block.type === "quote") return <blockquote key={index}>{block.text}</blockquote>;
                if (block.type === "callout")
                  return <aside className="rounded-xl bg-emerald-50 p-4" key={index}>{block.text}</aside>;
                return <p key={index}>{block.text}</p>;
              })}
            </article>
          </details>

          {selected && (
            <section className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div>
                <h2 className="font-serif text-2xl">Workflow decision</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Every transition is validated in the database and recorded in
                  the immutable workflow history.
                </p>
              </div>
              <Label>
                Decision reason
                <Input
                  minLength={8}
                  onChange={(event) => setChangeNote(event.target.value)}
                  placeholder="Reason for this workflow decision"
                  value={changeNote}
                />
              </Label>
              {selected.status === "approved" && (
                <Label>
                  Publication window
                  <Input
                    min={new Date().toISOString().slice(0, 16)}
                    onChange={(event) => setScheduledFor(event.target.value)}
                    type="datetime-local"
                    value={scheduledFor}
                  />
                </Label>
              )}
              <div className="flex flex-wrap gap-2">
                {workflowActions(selected).map((action) => (
                  <Button
                    disabled={
                      busy ||
                      changeNote.trim().length < 8 ||
                      (action.status === "scheduled" && !scheduledFor) ||
                      (selected.status === "scheduled" &&
                        action.status === "published" &&
                        !!selected.scheduled_for &&
                        new Date(selected.scheduled_for) > new Date())
                    }
                    key={action.status}
                    onClick={() => void move(action.status)}
                    type="button"
                    variant={action.status === "published" ? undefined : "outline"}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
              {selected.scheduled_for && (
                <p className="text-sm text-slate-500">
                  Current publication window: {formatDate(selected.scheduled_for)}
                </p>
              )}
            </section>
          )}

          <section className="grid gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="font-serif text-2xl">Authors and reviewers</h2>
            <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto] md:items-end">
              <Label>
                Display name
                <Input onChange={(event) => setAuthorName(event.target.value)} value={authorName} />
              </Label>
              <Label>
                Role or credentials
                <Input onChange={(event) => setAuthorRole(event.target.value)} value={authorRole} />
              </Label>
              <Button
                disabled={busy || authorName.trim().length < 2}
                onClick={() => void addAuthor()}
                type="button"
                variant="outline"
              >
                Add profile
              </Button>
            </div>
          </section>

          {selected && (
            <section className="grid gap-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:grid-cols-2">
              <div>
                <h2 className="font-serif text-2xl">Revision history</h2>
                <div className="mt-3 grid gap-2">
                  {revisions.slice(0, 12).map((revision) => (
                    <div className="rounded-xl bg-slate-50 p-3 text-sm" key={revision.id}>
                      <strong>Revision {revision.revision_number}</strong>
                      <p className="mt-1 text-slate-600">
                        {revision.change_note || "Legacy revision"}
                      </p>
                      <small className="text-slate-400">{formatDate(revision.created_at)}</small>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h2 className="font-serif text-2xl">Workflow history</h2>
                <div className="mt-3 grid gap-2">
                  {events.slice(0, 12).map((event) => (
                    <div className="rounded-xl bg-slate-50 p-3 text-sm" key={event.id}>
                      <strong>
                        {(event.from_status || "created").replaceAll("_", " ")} →{" "}
                        {event.to_status.replaceAll("_", " ")}
                      </strong>
                      <p className="mt-1 text-slate-600">{event.reason}</p>
                      <small className="text-slate-400">{formatDate(event.created_at)}</small>
                    </div>
                  ))}
                  {!events.length && (
                    <p className="text-sm text-slate-500">No governed transitions recorded yet.</p>
                  )}
                </div>
              </div>
            </section>
          )}
        </div>
      </section>
    </div>
  );
}
